import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { CheckCircle2, Pencil } from "lucide-react";
import {
  agruparPorBorda,
  painelBordeiroService,
  type PainelBordeiroDia,
  type ProducaoBorda,
} from "../../../services/painelBordeiroService";
import { publicUploadUrl } from "../../../utils/orientacaoProducaoImageUrl";
import { BrazilDate } from "../../../utils/BrazilDate";
import EditorProducao, { type LinhaEditor } from "./EditorProducao";
import UsoTamanho from "./ConsumoEstoque";
import { formatDataCompleta, formatDiaMes } from "../utils";

type Props = {
  dados: PainelBordeiroDia;
  queryKey: unknown[];
};

function chaveBT(bordaChave: string, tamanhoChave: string) {
  return `${bordaChave}|${tamanhoChave}`;
}

function linhasDaSugestao(dados: PainelBordeiroDia): LinhaEditor[] {
  return dados.sugestao.map((s) => ({
    bordaNome: s.bordaNome,
    bordaChave: s.bordaChave,
    tamanho: s.tamanho,
    tamanhoChave: s.tamanhoChave,
    quantidadeBase: s.quantidadeBase,
    quantidadeSugerida: s.quantidadeSugerida,
    quantidadePlanejada: s.quantidadeSugerida,
  }));
}

function linhasDaProducao(
  dados: PainelBordeiroDia,
  producao: ProducaoBorda
): LinhaEditor[] {
  const porChave = new Map(
    producao.itens.map((i) => [chaveBT(i.bordaChave, i.tamanhoChave), i])
  );
  const linhas: LinhaEditor[] = producao.itens.map((i) => ({
    bordaNome: i.bordaNome,
    bordaChave: i.bordaChave,
    tamanho: i.tamanho,
    tamanhoChave: i.tamanhoChave,
    quantidadeBase: i.quantidadeBase,
    quantidadeSugerida: i.quantidadeSugerida,
    quantidadePlanejada: i.quantidadePlanejada,
  }));
  for (const s of dados.sugestao) {
    if (!porChave.has(chaveBT(s.bordaChave, s.tamanhoChave))) {
      linhas.push({
        bordaNome: s.bordaNome,
        bordaChave: s.bordaChave,
        tamanho: s.tamanho,
        tamanhoChave: s.tamanhoChave,
        quantidadeBase: s.quantidadeBase,
        quantidadeSugerida: s.quantidadeSugerida,
        quantidadePlanejada: 0,
      });
    }
  }
  return linhas;
}

const VisaoDiaria: React.FC<Props> = ({ dados, queryKey }) => {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState(false);

  const salvar = useMutation({
    mutationFn: (linhas: LinhaEditor[]) =>
      painelBordeiroService.salvarProducao({
        data: dados.data,
        lojaId: dados.lojaId,
        percentual: dados.percentual,
        itens: linhas
          .filter((l) => l.quantidadePlanejada > 0 || l.quantidadeBase > 0)
          .map((l) => ({
            bordaNome: l.bordaNome,
            tamanho: l.tamanho,
            quantidadeBase: l.quantidadeBase,
            quantidadeSugerida: l.quantidadeSugerida,
            quantidadePlanejada: l.quantidadePlanejada,
          })),
      }),
    onSuccess: (resp) => {
      queryClient.setQueryData(queryKey, resp);
      setEditando(false);
      toast.success("Produção confirmada. Os cards já estão na tela Bordeiro.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const linhasSugestao = useMemo(() => linhasDaSugestao(dados), [dados]);
  const producao = dados.producao;
  const linhasEdicao = useMemo(
    () => (producao ? linhasDaProducao(dados, producao) : []),
    [dados, producao]
  );

  const confirmar = async (linhas: LinhaEditor[], edicao: boolean) => {
    const total = linhas.reduce((s, l) => s + l.quantidadePlanejada, 0);
    const alteradas = linhas.filter(
      (l) => l.quantidadePlanejada !== l.quantidadeSugerida
    ).length;
    const r = await Swal.fire({
      title: edicao ? "Salvar alterações?" : "Confirmar produção de bordas?",
      html:
        `<p>Total de <b>${total}</b> bordas para ${dados.diaSemana.toLowerCase()} (${formatDataCompleta(dados.data)}).</p>` +
        (alteradas > 0
          ? `<p class="mt-2 text-sm">${alteradas} borda(s) diferente(s) da sugestão do sistema.</p>`
          : `<p class="mt-2 text-sm">Seguindo a sugestão do sistema (${dados.percentual}% de ${formatDiaMes(dados.dataBase)}).</p>`),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: edicao ? "Salvar" : "Confirmar",
      cancelButtonText: "Voltar",
      confirmButtonColor: "#16a34a",
    });
    if (r.isConfirmed) salvar.mutate(linhas);
  };

  if (!producao) {
    if (!dados.podeGerarProducao) {
      return (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-6 text-sm text-neutral-600">
          Nenhuma produção de bordas foi registrada em{" "}
          {formatDataCompleta(dados.data)}.
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          Sugestão do sistema para <b>{dados.diaSemana}</b> (
          {formatDataCompleta(dados.data)}): <b>{dados.percentual}%</b> do que
          foi vendido em {dados.diaSemana.toLowerCase()} passada (
          {formatDataCompleta(dados.dataBase)}).{" "}
          {dados.podeAlterarQuadro
            ? "Confira, ajuste se o dia for diferente (chuva, feriado…) e confirme."
            : "Confira e confirme. Ajustes de quantidade são feitos por Administrador, Desenvolvedor ou Líderes."}{" "}
          Ao confirmar, os cards aparecem na tela Bordeiro.
        </div>
        {linhasSugestao.length === 0 ? (
          <p className="text-sm text-neutral-600">
            Não há vendas de bordas registradas no dia base.
          </p>
        ) : (
          <EditorProducao
            linhasIniciais={linhasSugestao}
            percentual={dados.percentual}
            dataBaseLabel={formatDiaMes(dados.dataBase)}
            salvando={salvar.isPending}
            podeEditar={dados.podeAlterarQuadro}
            textoConfirmar="Confirmar"
            onConfirmar={(l) => void confirmar(l, false)}
          />
        )}
      </div>
    );
  }

  const finalizada = producao.status === "finalizada";

  if (editando && !finalizada && dados.podeAlterarQuadro) {
    return (
      <EditorProducao
        linhasIniciais={linhasEdicao}
        percentual={producao.percentual}
        dataBaseLabel={formatDiaMes(producao.dataBase)}
        salvando={salvar.isPending}
        podeEditar
        textoConfirmar="Salvar alterações"
        onConfirmar={(l) => void confirmar(l, true)}
        onCancelar={() => setEditando(false)}
      />
    );
  }

  const grupos = agruparPorBorda(
    producao.itens.filter((i) => i.quantidadePlanejada > 0 || i.consumido > 0)
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 text-sm text-neutral-800">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div>
            <p className="font-semibold text-emerald-800">
              {finalizada
                ? "Produção finalizada"
                : "Confirmada — em produção na tela Bordeiro"}
            </p>
            <p className="text-xs text-neutral-600">
              Confirmada por {producao.confirmadoPorNome ?? "—"} em{" "}
              {BrazilDate.formatBR(new Date(producao.confirmadoEm), true)} ·
              base {producao.percentual}% de {formatDiaMes(producao.dataBase)}
            </p>
          </div>
        </div>
        {!finalizada && dados.podeAlterarQuadro && (
          <button
            type="button"
            onClick={() => setEditando(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <Pencil className="h-4 w-4" /> Editar quadro
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {grupos.map((g) => {
          const planejado = g.tamanhos.reduce(
            (s, i) => s + i.quantidadePlanejada,
            0
          );
          const saiu = g.tamanhos.reduce((s, i) => s + i.consumido, 0);
          return (
            <div
              key={g.bordaChave}
              className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-4 py-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase text-neutral-400">
                    Borda
                  </p>
                  <h3 className="font-bold text-neutral-900">{g.bordaNome}</h3>
                </div>
                <span className="text-sm font-bold tabular-nums text-primary">
                  {saiu}/{planejado}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 text-xs text-neutral-500">
                    <th className="px-4 py-2 text-left font-semibold">
                      Tamanho
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Produzir
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Saíram
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Restam
                    </th>
                    <th className="px-4 py-2 text-right font-semibold">Uso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {g.tamanhos.map((i) => (
                    <tr key={i.id}>
                      <td className="px-4 py-2 font-medium text-neutral-800">
                        {i.tamanho}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums">
                        {i.quantidadePlanejada}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-neutral-600">
                        {i.consumido}
                      </td>
                      <td
                        className={`px-2 py-2 text-right font-semibold tabular-nums ${
                          i.excedente > 0 ? "text-red-600" : "text-emerald-700"
                        }`}
                      >
                        {i.excedente > 0 ? `-${i.excedente}` : i.restante}
                      </td>
                      <td className="px-4 py-2">
                        <UsoTamanho
                          consumido={i.consumido}
                          planejado={i.quantidadePlanejada}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {finalizada && (
        <div className="flex flex-col gap-4 rounded-xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-center">
          <div className="flex-1 text-sm text-neutral-800">
            <p className="font-semibold">
              Assinado por {producao.responsavelNome}
            </p>
            {producao.finalizadoEm && (
              <p className="text-xs text-neutral-600">
                {BrazilDate.formatBR(new Date(producao.finalizadoEm), true)}
              </p>
            )}
            {producao.observacao && (
              <p className="mt-2 whitespace-pre-wrap">{producao.observacao}</p>
            )}
          </div>
          {producao.imagemAssinatura && (
            <img
              src={publicUploadUrl(producao.imagemAssinatura)}
              alt={`Assinatura de ${producao.responsavelNome ?? "responsável"}`}
              className="h-24 rounded-lg border border-neutral-200 bg-white object-contain p-1"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default VisaoDiaria;
